import React, { useState } from 'react';
import Text from 'components/atoms/Text';
import Spinner from 'components/atoms/Spinner';
import {
	Chart as ChartJS,
	CategoryScale,
	LinearScale,
	BarElement,
	Title,
	Tooltip,
	Legend,
	ChartOptions,
} from 'chart.js';
import { Bar } from 'react-chartjs-2';
import { useGetAlloc8SyncStatsQuery } from 'redux/api/vehicleIncidents';

ChartJS.register(
	CategoryScale,
	LinearScale,
	BarElement,
	Title,
	Tooltip,
	Legend
);

const formatDateLabel = (date: string): string => {
	const [year, month, day] = date.split('-');
	if (!year || !month || !day) return date;
	return `${day}/${month}`;
};

const IncidentMappingFailuresByDateGraph: React.FC = () => {
	const [selectedDays, setSelectedDays] = useState<7 | 14 | 30 | 90>(14);
	const { data, isLoading, error } = useGetAlloc8SyncStatsQuery({
		days: selectedDays,
	});

	const dayOptions: Array<7 | 14 | 30 | 90> = [7, 14, 30, 90];

	if (isLoading) {
		return (
			<div className="flex items-center justify-center h-full">
				<Spinner />
			</div>
		);
	}

	if (error) {
		return (
			<div className="flex items-center justify-center h-full">
				<Text className="text-red-600">Error loading data</Text>
			</div>
		);
	}

	const mappingFailuresByDate = data?.mappingFailuresByDate || [];

	if (!mappingFailuresByDate.length) {
		return (
			<div className="h-full flex flex-col">
				<div className="flex justify-end gap-2 mb-3">
					{dayOptions.map((days) => (
						<button
							key={days}
							type="button"
							onClick={() => setSelectedDays(days)}
							className={`px-2 py-1 rounded text-[12px] font-semibold border ${
								selectedDays === days
									? 'bg-primary text-white border-primary'
									: 'bg-white text-gray-700 border-gray-300'
							}`}
						>
							{days}d
						</button>
					))}
				</div>
				<div className="flex items-center justify-center h-full">
					<Text className="text-[14px] text-gray-500">
						No mapping failures in the last {selectedDays} days.
					</Text>
				</div>
			</div>
		);
	}

	const options: ChartOptions<'bar'> = {
		plugins: {
			legend: {
				display: false,
			},
			tooltip: {
				displayColors: false,
				callbacks: {
					label: (context) => `Count: ${context.raw || 0}`,
				},
			},
		},
		responsive: true,
		maintainAspectRatio: false,
		scales: {
			x: {
				title: {
					display: true,
					text: 'Date',
				},
			},
			y: {
				beginAtZero: true,
				ticks: {
					precision: 0,
				},
				title: {
					display: true,
					text: 'Count',
				},
			},
		},
	};

	const chartData = {
		labels: mappingFailuresByDate.map((entry) => formatDateLabel(entry.date)),
		datasets: [
			{
				label: 'Incident Mapping Failures',
				data: mappingFailuresByDate.map((entry) => entry.count),
				backgroundColor: '#F97316',
				borderRadius: 4,
			},
		],
	};

	return (
		<div className="h-full flex flex-col">
			<div className="flex justify-end gap-2 mb-3">
				{dayOptions.map((days) => (
					<button
						key={days}
						type="button"
						onClick={() => setSelectedDays(days)}
						className={`px-2 py-1 rounded text-[12px] font-semibold border ${
							selectedDays === days
								? 'bg-primary text-white border-primary'
								: 'bg-white text-gray-700 border-gray-300'
						}`}
					>
						{days}d
					</button>
				))}
			</div>
			<div className="flex-1 min-h-0">
				<Bar options={options} data={chartData} />
			</div>
		</div>
	);
};

export default IncidentMappingFailuresByDateGraph;
